import { h } from '@omni-torn/ui';
import { LEND_SHARE_DEFAULT, LEND_SHARE_MAX, LEND_SHARE_MIN, LEND_WHAT, lendNeedsTermsText, TERMS_VERSION, type LendCalls, type LendStatus } from '@omni-torn/shared';
import { server, session } from '../session';
import { box, tct, withTableCsv } from '../ui';
import { termsTable } from './keys';

/**
 * "Lend my key" on Account (lent keys, terms version 8; server apps/api/src/lent-keys.ts, /api/me/lend): the switch, the
 * share a minute, "my faction too", an optional separate key, and what the key did (last hour / day, by use). Stays off,
 * saying why, until the player agreed to the terms version that names lending. Stopping deletes the lending at once.
 */
export function lendBox(): HTMLElement {
  const notice = h('div', { class: 'lend-state', role: 'status' }, h('p', { class: 'muted' }, 'Loading…'));
  const msg = h('p', { class: 'form-status', role: 'status' });
  const on = h('input', { type: 'checkbox', class: 'lend-on', disabled: true }) as HTMLInputElement;
  const share = h('input', { type: 'range', class: 'lend-share', min: String(LEND_SHARE_MIN), max: String(LEND_SHARE_MAX), step: '1', value: String(LEND_SHARE_DEFAULT), 'aria-label': 'Calls a minute others may make with your key', disabled: true }) as HTMLInputElement;
  const shareOut = h('output', { class: 'lend-share-out' }, `${LEND_SHARE_DEFAULT} calls a minute`);
  const faction = h('input', { type: 'checkbox', class: 'lend-faction', disabled: true }) as HTMLInputElement;
  const other = h('input', { type: 'password', class: 'lend-key', autocomplete: 'off', maxlength: '16', placeholder: 'Optional: a separate key just for lending', 'aria-label': 'A separate key just for lending', disabled: true }) as HTMLInputElement;
  const keyNote = h('p', { class: 'muted small lend-key-note' });
  const did = h('div', { class: 'lend-did' });
  let last: LendStatus | null = null;

  const callsTable = (c: LendCalls): HTMLElement => {
    if (!c.dayTotal) return h('p', { class: 'muted' }, 'Nothing yet in the last day.');
    const uses = [...new Map(c.day.map((x) => [x.use, x.name])).entries()];
    const n = (list: LendCalls['hour'], use: string) => String(list.find((x) => x.use === use)?.n ?? 0);
    return withTableCsv(h(
      'div',
      { class: 'table-wrap' },
      h(
        'table',
        { class: 'lend-calls' },
        h('thead', {}, h('tr', {}, h('th', {}, 'Used for'), h('th', { class: 'num' }, 'Last hour'), h('th', { class: 'num' }, 'Last day'))),
        h('tbody', {}, ...uses.map(([use, name]) => h('tr', {}, h('td', {}, name), h('td', { class: 'num' }, n(c.hour, use)), h('td', { class: 'num' }, n(c.day, use)))), h('tr', {}, h('th', { scope: 'row' }, 'All'), h('td', { class: 'num' }, String(c.hourTotal)), h('td', { class: 'num' }, String(c.dayTotal)))),
      ),
    ));
  };

  const show = (r: LendStatus) => {
    last = r;
    const can = r.available && r.agreed;
    on.checked = r.on;
    on.disabled = !can && !r.on;
    share.value = String(r.share);
    shareOut.textContent = `${r.share} calls a minute`;
    faction.checked = r.faction;
    share.disabled = faction.disabled = other.disabled = !can;
    keyNote.textContent = r.on
      ? `Lent: ${r.source === 'separate' ? 'a separate key you gave for it' : 'the key Omni-Torn keeps for your account'}${r.since ? `, since ${tct(r.since)} TCT` : ''}. A key typed here (then Save) replaces it.`
      : r.hasAccountKey
        ? 'Leave the box empty to lend the key Omni-Torn keeps for your account, or give a separate key just for lending.'
        : 'Omni-Torn keeps no key for your account, so the key you signed in with here is lent — or give a separate one.';
    const lines: HTMLElement[] = [];
    if (!r.available) lines.push(h('p', { class: 'notice' }, 'Lending isn’t available on this server right now (its key store is missing).'));
    else if (!r.agreed) lines.push(h('p', { class: 'notice lend-terms' }, lendNeedsTermsText(TERMS_VERSION)));
    if (r.status === 'ok') lines.push(h('p', { 'data-lend': 'on' }, h('span', { class: 't-green' }, '✓ '), `Lent: up to ${r.share} calls a minute for public Torn data${r.faction ? ', and your faction’s data for your faction' : ''}.`));
    else if (r.status === 'resting') lines.push(h('p', { class: 'notice', 'data-lend': 'resting' }, `Resting until ${r.restUntil ? tct(r.restUntil) : 'soon'} TCT: ${r.problem ?? ''}`));
    else if (r.status === 'bad') lines.push(h('p', { class: 'notice bad', 'data-lend': 'stopped' }, `Lending stopped: ${r.problem ?? 'Torn no longer accepts the key.'}`));
    else lines.push(h('p', { 'data-lend': 'off' }, 'Not lent.'));
    if (r.faction && r.on && !r.factionAccess) lines.push(h('p', { class: 'muted' }, 'Your key can’t read your faction’s data (your faction’s “API access” permission), so it’s used only for public data.'));
    notice.replaceChildren(...lines);
    did.replaceChildren(callsTable(r.calls));
  };

  const load = () => server<LendStatus>('GET', '/api/me/lend').then(show, (err: Error) => notice.replaceChildren(h('p', { class: 'notice bad' }, err.message)));

  const save = async (want: { on: boolean }, working: string, done: string) => {
    msg.className = 'form-status';
    msg.textContent = working;
    try {
      const typed = other.value.trim();
      const s = session.get();
      // Lending with no key kept for the account: the key typed on this site (with the extension it never comes here).
      const signInKey = !typed && !last?.hasAccountKey && !last?.on && s?.mode === 'key' ? s.key : undefined;
      const body: Record<string, unknown> = { seen: 'terms', on: want.on, share: Number(share.value), faction: faction.checked };
      if (typed) Object.assign(body, { key: typed, separate: true });
      else if (signInKey) body.key = signInKey;
      const r = await server<LendStatus>('PUT', '/api/me/lend', body);
      other.value = '';
      show(r);
      msg.className = 'form-status ok';
      msg.textContent = done;
    } catch (err) {
      msg.className = 'form-status bad';
      msg.textContent = (err as Error).message;
      if (last) show(last);
    }
  };
  on.addEventListener('change', () => void (on.checked ? save({ on: true }, 'Checking your key with Torn…', 'Lent — thank you! Stop any time.') : save({ on: false }, 'Stopping…', 'Stopped: the lending is deleted.')));
  share.addEventListener('input', () => (shareOut.textContent = `${share.value} calls a minute`));
  share.addEventListener('change', () => void (last?.on ? save({ on: true }, 'Saving…', `Share saved: ${share.value} calls a minute.`) : null));
  faction.addEventListener('change', () => void (last?.on ? save({ on: true }, 'Saving…', faction.checked ? 'Your faction gets it too.' : 'Public data only now.') : null));
  const saveKey = h('button', { type: 'button', class: 'btn-small lend-key-save' }, 'Save');
  saveKey.addEventListener('click', () => void (other.value.trim() ? save({ on: true }, 'Checking your key with Torn…', 'Lent with that key.') : null));

  void load();
  return box(
    'Lend my key',
    [
      h('p', { class: 'muted' }, LEND_WHAT),
      // Torn's terms: the table wherever a key is given (and lending is a use of it).
      h('details', { class: 'terms', open: true }, h('summary', {}, 'What Omni-Torn does with your key (Torn’s API terms)'), termsTable()),
      notice,
      h('label', { class: 'switch' }, on, h('strong', {}, ' Lend my key to Omni-Torn')),
      h('p', { class: 'lend-share-row' }, h('label', {}, 'Others may use it for up to ', share, ' ', shareOut), h('span', { class: 'muted small' }, ` (${LEND_SHARE_MIN}–${LEND_SHARE_MAX}; all Omni-Torn does with your keys stays under 60 a minute — Torn allows 100)`)),
      h('label', { class: 'check' }, faction, ' My faction too — the Discord bot reads my own faction’s data with it, for my faction only'),
      h('p', {}, other, ' ', saveKey),
      keyNote,
      msg,
      h('h3', {}, 'What my key did'),
      did,
    ],
    { cls: 'lend-box', id: 'lend' },
  );
}
